/// <reference types="@types/google.maps" />
import React, { useMemo, useCallback, useEffect, useRef, useState } from 'react';
import { GoogleMap, useLoadScript, Marker, DirectionsRenderer } from '@react-google-maps/api';
import { Box, Typography } from '@mui/material';

// Declare the google maps types
declare global {
  interface Window {
    google: {
      maps: {
        Map: new (element: HTMLElement, options: google.maps.MapOptions) => google.maps.Map;
        DirectionsService: new () => google.maps.DirectionsService;
        DirectionsRenderer: new (options?: google.maps.DirectionsRendererOptions) => google.maps.DirectionsRenderer;
        TravelMode: {
          WALKING: google.maps.TravelMode;
        };
        marker: {
          AdvancedMarkerElement: new (options: google.maps.marker.AdvancedMarkerElementOptions) => google.maps.marker.AdvancedMarkerElement;
        };
      };
    };
  }
}

interface Coordinates {
  lat: number;
  lng: number;
}

interface TourStop {
  pointOfInterest: string;
  coordinates: Coordinates | null;
}

interface TourMapProps {
  tourStops: TourStop[];
  selectedStop: number | null;
  onStopSelect: (index: number) => void;
}

const libraries: ("places" | "drawing" | "geometry" | "visualization")[] = ['places'];

// Memoize the map options to prevent re-renders
const mapOptions = {
  disableDefaultUI: true,
  zoomControl: true,
  mapTypeControl: false,
  streetViewControl: false,
  fullscreenControl: false,
  styles: [
    {
      featureType: "all",
      elementType: "all",
      stylers: [
        { saturation: -100 },
        { lightness: 0 }
      ]
    },
    {
      featureType: "water",
      elementType: "geometry",
      stylers: [
        { color: "#e9e9e9" },
        { lightness: 17 }
      ]
    },
    {
      featureType: "landscape",
      elementType: "geometry",
      stylers: [
        { color: "#f5f5f5" },
        { lightness: 20 }
      ]
    },
    {
      featureType: "road.highway",
      elementType: "geometry.fill",
      stylers: [
        { color: "#ffffff" },
        { lightness: 17 }
      ]
    },
    {
      featureType: "road.highway",
      elementType: "geometry.stroke",
      stylers: [
        { color: "#ffffff" },
        { lightness: 29 },
        { weight: 0.2 }
      ]
    },
    {
      featureType: "road.arterial",
      elementType: "geometry",
      stylers: [
        { color: "#ffffff" },
        { lightness: 18 }
      ]
    },
    {
      featureType: "road.local",
      elementType: "geometry",
      stylers: [
        { color: "#ffffff" },
        { lightness: 16 }
      ]
    },
    {
      featureType: "poi",
      elementType: "geometry",
      stylers: [
        { color: "#f5f5f5" },
        { lightness: 21 }
      ]
    },
    {
      featureType: "poi.park",
      elementType: "geometry",
      stylers: [
        { color: "#dedede" },
        { lightness: 21 }
      ]
    }
  ]
};

const TourMap: React.FC<TourMapProps> = React.memo(({ tourStops, selectedStop, onStopSelect }) => {
  const mapRef = useRef<google.maps.Map | null>(null);
  const [directions, setDirections] = useState<google.maps.DirectionsResult[]>([]);
  const directionsService = useRef<google.maps.DirectionsService | null>(null);
  const [userLocation, setUserLocation] = useState<Coordinates | null>(null);
  const [showUserLocation, setShowUserLocation] = useState(false);

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
    libraries
  });

  const validStops = useMemo(() => 
    tourStops.filter(stop => stop.coordinates !== null),
    [tourStops]
  );

  const center = useMemo(() => {
    if (!isLoaded || validStops.length === 0) {
      return { lat: 37.7749, lng: -122.4194 }; // Default to San Francisco
    }
    
    const bounds = new google.maps.LatLngBounds();
    validStops.forEach(stop => {
      if (stop.coordinates) {
        bounds.extend(stop.coordinates);
      }
    });
    
    return bounds.getCenter().toJSON();
  }, [validStops, isLoaded]);

  const calculateWalkingRoutes = useCallback(async () => {
    if (!directionsService.current || validStops.length < 2) return;

    const newDirections: google.maps.DirectionsResult[] = [];
    
    for (let i = 0; i < validStops.length - 1; i++) {
      const start = validStops[i].coordinates!;
      const end = validStops[i + 1].coordinates!;

      try {
        const result = await directionsService.current.route({
          origin: start,
          destination: end,
          travelMode: google.maps.TravelMode.WALKING
        });
        newDirections.push(result);
      } catch (error) {
        console.error('Error calculating walking route:', error);
      }
    }

    setDirections(newDirections);
  }, [validStops]);

  const createPinEmojiMarker = useCallback((index: number, isSelected: boolean): google.maps.Icon => {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) {
      // Fallback to default marker if canvas is not available
      return {
        url: 'http://maps.google.com/mapfiles/ms/icons/red-dot.png',
        scaledSize: new google.maps.Size(40, 40),
        anchor: new google.maps.Point(20, 40)
      };
    }

    // Set canvas size
    canvas.width = 40;
    canvas.height = 40;

    // Draw emoji
    context.font = '30px Arial';
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    context.fillText('📍', 20, 20);

    // Draw number
    context.font = 'bold 14px Arial';
    context.fillStyle = 'white';
    context.fillText((index + 1).toString(), 20, 20);

    return {
      url: canvas.toDataURL(),
      scaledSize: new google.maps.Size(40, 40),
      anchor: new google.maps.Point(20, 40)
    };
  }, []);

  const onMapLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
    directionsService.current = new google.maps.DirectionsService();
    
    if (validStops.length > 0) {
      const bounds = new google.maps.LatLngBounds();
      validStops.forEach(stop => {
        if (stop.coordinates) {
          bounds.extend(stop.coordinates);
        }
      });
      
      map.fitBounds(bounds, {
        top: 50,
        right: 50,
        bottom: 50,
        left: 50
      });

      const listener = map.addListener('idle', () => {
        const zoom = map.getZoom();
        if (zoom && zoom > 16) {
          map.setZoom(16);
        }
        google.maps.event.removeListener(listener);
      });
    }

    calculateWalkingRoutes();
  }, [validStops, calculateWalkingRoutes]);

  // Only update bounds when validStops changes
  useEffect(() => {
    if (mapRef.current && validStops.length > 0) {
      const bounds = new google.maps.LatLngBounds();
      validStops.forEach(stop => {
        if (stop.coordinates) {
          bounds.extend(stop.coordinates);
        }
      });
      
      mapRef.current.fitBounds(bounds, {
        top: 50,
        right: 50,
        bottom: 50,
        left: 50
      });

      calculateWalkingRoutes();
    } else {
      setDirections([]);
    }
  }, [validStops, calculateWalkingRoutes]);

  const handleToggleUserLocation = () => {
    if (showUserLocation) {
      // Hide marker
      setShowUserLocation(false);
    } else {
      // Show marker: get location if we haven't already
      if (userLocation) {
        setShowUserLocation(true);
      } else {
        // Check if user is on Safari mobile before requesting location
        const isSafariMobile = /iPhone|iPad|iPod/i.test(navigator.userAgent) && /Safari/i.test(navigator.userAgent);
        
        if (isSafariMobile) {
          const proceed = window.confirm(
            "Before we request your location, please ensure location services are enabled:\n\n" +
            "1. Settings > Privacy & Security > Location Services > Safari Websites > Select 'While Using'\n\n" +
            "2. Settings > Safari > Location > Select 'Allow'\n\n" +
            "3. Close Safari and reload this page\n\n" +
            "Press OK once you've confirmed these settings."
          );
          
          if (!proceed) return;
        }

        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(
            (position) => {
              const coords = {
                lat: position.coords.latitude,
                lng: position.coords.longitude
              };
              setUserLocation(coords);
              setShowUserLocation(true);

              if (mapRef.current) {
                mapRef.current.panTo(coords);
                mapRef.current.setZoom(15);
              }
            },
            (error) => {
              console.error("Error getting user location:", error);
              alert("Unable to retrieve your location. Please check your browser settings and try again.");
            }
          );
        } else {
          alert("Geolocation is not supported by your browser.");
        }
      }
    }
  };

  if (loadError) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography color="error">Error loading map</Typography>
      </Box>
    );
  }

  if (!isLoaded) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography>Loading map...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
      <Box sx={{ position: 'absolute', top: 16, right: 16, zIndex: 10 }}>
        <button
          onClick={handleToggleUserLocation}
          style={{
            backgroundColor: showUserLocation ? '#DB4437' : '#4ECDC4',
            color: 'white',
            padding: '8px 16px',
            borderRadius: '8px',
            border: 'none',
            cursor: 'pointer',
            fontWeight: 600,
            transition: 'all 0.2s ease',
            boxShadow: 'none'
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.backgroundColor = showUserLocation ? '#C53929' : '#45b8b0';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(78, 205, 196, 0.2)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.backgroundColor = showUserLocation ? '#DB4437' : '#4ECDC4';
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          {showUserLocation ? 'Hide My Location' : 'Show My Location'}
        </button>
      </Box>
      <GoogleMap
        mapContainerStyle={{ width: '100%', height: '100%' }}
        center={center}
        options={mapOptions}
        onLoad={onMapLoad}
      >
        {validStops.map((stop, index) => (
          stop.coordinates && (
            <Marker
              key={index}
              position={stop.coordinates}
              label={{
                text: (index + 1).toString(),
                color: 'white',
                fontWeight: 'bold'
              }}
              onClick={() => onStopSelect(index)}
            />
          )
        ))}
        {directions.map((direction, index) => (
          <DirectionsRenderer
            key={index}
            directions={direction}
            options={{
              suppressMarkers: true,
              preserveViewport: true,
              polylineOptions: {
                strokeColor: '#4ECDC4',
                strokeWeight: 4,
                strokeOpacity: 0.7
              }
            }}
          />
        ))}
        {showUserLocation && userLocation && (
          <Marker
            position={userLocation}
            icon={{
              path: google.maps.SymbolPath.CIRCLE,
              scale: 10,
              fillColor: '#4285F4',
              fillOpacity: 1,
              strokeColor: '#fff',
              strokeWeight: 2,
            }}
            title="You are here"
          />
        )}
      </GoogleMap>
    </Box>
  );
}, (prevProps, nextProps) => {
  // Custom comparison function for React.memo
  return (
    prevProps.tourStops === nextProps.tourStops &&
    prevProps.selectedStop === nextProps.selectedStop
  );
});

TourMap.displayName = 'TourMap';

export default TourMap; 